import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PostSignUpForm } from '../types';
import { useForm } from 'react-hook-form';
import iconLogo from '../assets/icon.png';
import { 
  AcademicCapIcon, 
  BookOpenIcon, 
  UserIcon, 
  ShoppingBagIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';

const PostSignUpPage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { currentUser, updateProfile } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostSignUpForm>();

  // Different options based on user role
  const getOptions = () => {
    if (currentUser?.role === 'author') {
      if (currentUser?.status === 'pending') {
        // Pending author - waiting for admin approval
        return [
          {
            id: 'pending_approval',
            title: 'Account Pending Approval',
            subtitle: 'Admin Review Required',
            description: 'Your author account is currently under review by our admin team',
            icon: AcademicCapIcon,
            color: 'yellow',
            features: [
              'Account under review',
              'Admin verification in progress',
              'Email notification when approved',
              'Estimated 24-48 hour review time',
              'Contact support if needed'
            ],
            price: 'Free',
            popular: false
          },
          {
            id: 'contact_support',
            title: 'Contact Support',
            subtitle: 'Get Help',
            description: 'Need assistance or have questions about your application?',
            icon: BookOpenIcon,
            color: 'blue',
            features: [
              'Email support',
              'Live chat assistance',
              'Application status updates',
              'Documentation help',
              'Quick response time'
            ],
            price: 'Free',
            popular: false
          }
        ];
      }
      
      // Approved author - can access author features
      return [
        {
          id: 'create_course',
          title: 'Create Your First Course',
          subtitle: 'Start Building Content',
          description: 'Begin creating and publishing your professional courses',
          icon: AcademicCapIcon,
          color: 'blue',
          features: [
            'Course creation tools',
            'Content management system',
            'Student analytics',
            'Revenue sharing',
            'Author dashboard'
          ],
          price: 'Free to start',
          popular: true
        },
        {
          id: 'author_dashboard',
          title: 'Access Author Dashboard',
          subtitle: 'Manage Your Content',
          description: 'View your published courses and student progress',
          icon: BookOpenIcon,
          color: 'green',
          features: [
            'Course management',
            'Student insights',
            'Performance metrics',
            'Content analytics',
            'Revenue tracking'
          ],
          price: 'Free',
          popular: false
        },
        {
          id: 'guest',
          title: 'Continue as Guest',
          subtitle: 'Limited Access',
          description: 'Explore the platform before creating content',
          icon: UserIcon,
          color: 'gray',
          features: [
            'Platform exploration',
            'Course browsing',
            'Community preview',
            'Upgrade anytime',
            'No commitment required'
          ],
          price: 'Free',
          popular: false
        }
      ];
    }

    // Default student options
    return [
      {
        id: 'ICAG',
        title: 'Register for ICAG',
        subtitle: 'Institute of Chartered Accountants Ghana',
        description: 'Master accounting, auditing, and financial management',
        icon: AcademicCapIcon,
        color: 'blue',
        features: [
          'Professional accounting courses',
          'Expert-led instruction',
          'Study materials & resources',
          'Discord community access',
          'Progress tracking'
        ],
        price: 'Free to start',
        popular: true
      },
      {
        id: 'CITG',
        title: 'Register for CITG',
        subtitle: 'Chartered Institute of Taxation Ghana',
        description: 'Develop expertise in tax law and compliance',
        icon: BookOpenIcon,
        color: 'green',
        features: [
          'Tax law & compliance courses',
          'Practical tax applications',
          'Industry expert guidance',
          'Discord community access',
          'Progress tracking'
        ],
        price: 'Free to start',
        popular: false
      },
      {
        id: 'books',
        title: 'Buy Books & Materials',
        subtitle: 'Digital Library Access',
        description: 'Purchase individual books and study materials',
        icon: ShoppingBagIcon,
        color: 'purple',
        features: [
          'Digital book collection',
          'Video lecture series',
          'Study guides & notes',
          'Lifetime access',
          'Mobile-friendly format'
        ],
        price: 'Pay per item',
        popular: false
      },
      {
        id: 'guest',
        title: 'Continue as Guest',
        subtitle: 'Limited Access',
        description: 'Explore free content without full registration',
        icon: UserIcon,
        color: 'gray',
        features: [
          'Free course previews',
          'Limited study materials',
          'Basic community access',
          'Upgrade anytime',
          'No commitment required'
        ],
        price: 'Free',
        popular: false
      }
    ];
  };

  const options = getOptions();

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
      green: 'border-green-200 bg-green-50 hover:bg-green-100',
      purple: 'border-purple-200 bg-purple-50 hover:bg-purple-100',
      gray: 'border-gray-200 bg-gray-50 hover:bg-gray-100'
    };
    return colorMap[color] || 'border-gray-200 bg-gray-50 hover:bg-gray-100';
  };

  const getIconColor = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-green-600 bg-green-100',
      purple: 'text-purple-600 bg-purple-100',
      gray: 'text-gray-600 bg-gray-100'
    };
    return colorMap[color] || 'text-gray-600 bg-gray-100';
  };

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const onSubmit = async (data: PostSignUpForm) => {
    if (!selectedOption || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      if (currentUser?.role === 'author') {
        if (currentUser?.status === 'pending') {
          // Handle pending author options
          if (selectedOption === 'pending_approval') {
            toast('Your account is under review. Please wait for admin approval.');
            setTimeout(() => {
              navigate('/app/author/pending-approval');
            }, 1500);
          } else if (selectedOption === 'contact_support') {
            toast('Need help? Contact our support team.');
            setTimeout(() => {
              navigate('/contact');
            }, 1500);
          }
        } else {
          // Handle approved author options
          if (selectedOption === 'create_course') {
            // Redirect to course creation
            toast.success('Welcome to the Author Dashboard!');
            setTimeout(() => {
              navigate('/app/author/create-course');
            }, 1500);
          } else if (selectedOption === 'author_dashboard') {
            // Redirect to author dashboard
            toast.success('Welcome to your Author Dashboard!');
            setTimeout(() => {
              navigate('/app/author/dashboard');
            }, 1500);
          } else if (selectedOption === 'guest') {
            // Update user profile as guest author
            await updateProfile({
              status: 'Offline',
              profileComplete: true
            });
            
            toast.success('Welcome! You can explore the platform before creating content.');
            setTimeout(() => {
              navigate('/app');
            }, 1500);
          }
        }
      } else {
        // Handle student options (existing logic)
        if (selectedOption === 'ICAG' || selectedOption === 'CITG') {
          // Update user profile with program selection
          await updateProfile({
            program: selectedOption,
            status: 'Online',
            profileComplete: true
          });
          
          toast.success(`Welcome to ${selectedOption}! Redirecting to courses catalog...`);
          setTimeout(() => {
            navigate('/catalog');
          }, 1500);
        } else if (selectedOption === 'books') {
          // Redirect to shop
          navigate('/app/shop');
        } else if (selectedOption === 'guest') {
          // Update user profile as guest
          await updateProfile({
            status: 'Offline',
            profileComplete: true
          });
          
          toast.success('Welcome! You can explore our free content.');
          setTimeout(() => {
            navigate('/app');
          }, 1500);
        }
      }
    } catch (error) {
      toast.error('Failed to complete profile setup');
      console.error('Profile setup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
                      <Link to="/" className="inline-flex items-center mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                <img src={iconLogo} alt="ProfStudy Logo" className="w-10 h-10 rounded-lg" />
            </div>
              <span className="text-2xl font-bold text-gray-900">ProfStudy</span>
            </Link>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to ProfStudy{currentUser?.role === 'author' ? ', Author!' : '!'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {currentUser?.role === 'author' 
              ? currentUser?.status === 'pending'
                ? "Your author account has been created and is pending admin approval. You'll receive an email once approved."
                : "Great! Your author account has been created successfully. Now choose how you'd like to start creating and sharing your professional knowledge."
              : "Great! Your account has been created successfully. Now choose how you'd like to proceed with your professional development journey."
            }
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {options.map((option) => (
            <div
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
                selectedOption === option.id
                  ? 'border-primary-500 bg-primary-50 shadow-lg scale-105'
                  : getColorClasses(option.color)
              }`}
            >
              {option.popular && (
                <div className="absolute -top-3 left-6 bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Most Popular
                </div>
              )}
              
              <div className="flex items-start mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${getIconColor(option.color)}`}>
                  <option.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {option.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {option.subtitle}
                  </p>
                  <p className="text-gray-700">
                    {option.description}
                  </p>
                </div>
              </div>
              
              <div className="mb-4">
                <ul className="space-y-2">
                  {option.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">
                  {option.price}
                </span>
                {selectedOption === option.id && (
                  <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                    <CheckIcon className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action Section */}
        {selectedOption && (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                You've selected: {options.find(opt => opt.id === selectedOption)?.title}
              </h2>
              <p className="text-gray-600">
                {selectedOption === 'ICAG' || selectedOption === 'CITG' 
                  ? 'Complete your registration to access the courses catalog and start learning.'
                  : selectedOption === 'books'
                  ? 'Browse our collection of books and study materials.'
                  : 'Explore our free content and upgrade anytime.'
                }
              </p>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Terms and Conditions */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="agreeToTerms"
                    type="checkbox"
                    {...register('agreeToTerms', {
                      required: 'You must agree to the terms and conditions'
                    })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="agreeToTerms" className="text-gray-700">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                      Privacy Policy
                    </Link>
                  </label>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-red-600">{errors.agreeToTerms.message}</p>
                  )}
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Setting up your account...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Help Section */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Need help choosing? Our team is here to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-outline"
            >
              Contact Support
            </Link>
            <Link
              to="/faq"
              className="btn-outline"
            >
              View FAQ
            </Link>
          </div>
        </div>

        {/* AI Chat Button */}
        <AIChatButton />
      </div>
    </div>
  );
};

export default PostSignUpPage;
